<template>
  <div class="w-full">
    <LayupSpinner v-if="isLoading" class="py-8" />
    <template v-else-if="step === 'form'">
      <div v-if="errorMessage" class="rounded-md w-full bg-red-100 px-4 py-3 text-sm text-red-700 mb-4">
        {{ errorMessage }}
      </div>

      <div class="flex items-center cursor-pointer mb-4" @click="emit('back')">
        <ArrowLeft class="h-5 w-5 shrink-0" />
        <span class="flex-grow text-xl font-semibold text-center">DebiCheck</span>
      </div>

      <form @submit.prevent="onSubmit">
        <div class="space-y-4 pb-4">
          <h1 class="text-xl font-semibold text-black">Personal Information</h1>

          <FormField v-slot="{ componentField }" name="firstName">
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input type="text" placeholder="John" v-bind="componentField" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="surname">
            <FormItem>
              <FormLabel>Surname</FormLabel>
              <FormControl>
                <Input type="text" placeholder="Doe" v-bind="componentField" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="cellNumber">
            <FormItem>
              <FormLabel>Mobile Number</FormLabel>
              <FormControl>
                <Input type="text" placeholder="0711000000" v-bind="componentField" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="identityType">
            <FormItem>
              <FormLabel>Identification Type</FormLabel>
              <Select v-bind="componentField">
                <FormControl>
                  <SelectTrigger><SelectValue placeholder="Identity Type" /></SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem v-for="type in identificationSelection" :key="type.value" :value="type.value">
                      {{ type.text }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="identificationNumber">
            <FormItem>
              <FormLabel>ID Number</FormLabel>
              <FormControl>
                <Input type="text" placeholder="8504121234081" v-bind="componentField" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>

        <div class="space-y-4 pb-4">
          <h1 class="text-xl font-semibold text-black">Payment Information</h1>

          <div class="grid grid-cols-2 gap-4">
            <FormField v-slot="{ componentField }" name="billingDay">
              <FormItem>
                <FormLabel>Billing Date</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="2020-12-28" v-bind="componentField" disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField v-slot="{ componentField }" name="instalmentAmount">
              <FormItem>
                <FormLabel>Amount</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="500" v-bind="componentField" disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>

          <div class="grid grid-cols-3 gap-4">
            <div class="col-span-2">
              <FormField v-slot="{ componentField }" name="bankName">
                <FormItem>
                  <FormLabel>Bank Name</FormLabel>
                  <Select v-bind="componentField">
                    <FormControl>
                      <SelectTrigger><SelectValue placeholder="Bank Name" /></SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem v-for="bank in bankSelection" :key="bank.value" :value="bank.value">
                          {{ bank.text }}
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>

            <FormField v-slot="{ componentField }" name="branchCode">
              <FormItem>
                <FormLabel>Branch Code</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="632005" v-bind="componentField" disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>

          <div class="grid grid-cols-5 gap-4">
            <div class="col-span-2">
              <FormField v-slot="{ componentField }" name="accountType">
                <FormItem>
                  <FormLabel>Account Type</FormLabel>
                  <Select v-bind="componentField">
                    <FormControl>
                      <SelectTrigger><SelectValue placeholder="Account Type" /></SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem v-for="type in accountTypeSelection" :key="type.value" :value="type.value">
                          {{ type.text }}
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>

            <div class="col-span-3">
              <FormField v-slot="{ componentField }" name="accountNumber">
                <FormItem>
                  <FormLabel>Account Number</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="8704123456" v-bind="componentField" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>
          </div>
        </div>

        <div class="space-y-3 pt-2">
          <div v-if="ENVIRONMENT !== 'production'" class="flex items-center gap-2">
            <input
              type="checkbox"
              v-model="allowSimulation"
              id="simulation-checkbox"
              class="rounded focus:ring-brand-500"
            />
            <label class="text-sm text-gray-500" for="simulation-checkbox">Simulation</label>
          </div>

          <div class="flex items-start gap-2">
            <input
              type="checkbox"
              v-model="acceptTerms"
              id="terms-checkbox"
              class="rounded mt-0.5 focus:ring-brand-500"
            />
            <label class="text-sm text-gray-500" for="terms-checkbox">
              I will allow LayUp to share this information with the relevant banks so that they can set up my debit
              order.
            </label>
          </div>

          <Button class="w-full" type="submit" :disabled="!acceptTerms || isLoading">Submit</Button>
        </div>
      </form>
    </template>

    <!-- Mandate confirmation after submission -->
    <template v-else-if="step === 'confirmation'">
      <div class="flex flex-col items-center justify-center py-8 text-center min-h-[260px]">
        <template v-if="timerCount > 0 && debiCheckStatus === null">
          <h1 class="text-xl font-bold mb-4">Verifying Mandate</h1>
          <div class="w-24 h-24 rounded-full border-4 border-teal-500 flex items-center justify-center mb-4">
            <span class="text-2xl font-semibold">{{ timerCount }}</span>
          </div>
          <p class="text-gray-500 text-sm">Please wait while we verify your debit order mandate</p>
        </template>

        <template v-else-if="debiCheckStatus === 'Pending Authorisation'">
          <template v-if="isAcceptMandateLater">
            <p class="text-gray-600 mb-4">You have until 9pm tomorrow to accept the debit order mandate.</p>
            <Button variant="outline" @click="emit('back')">Close</Button>
          </template>
          <template v-else>
            <div class="w-32 h-32 rounded-full border-4 border-teal-500 flex items-center justify-center mb-4">
              <span class="text-xl font-semibold">{{ countdownTime }}</span>
            </div>
            <p class="text-gray-600 text-sm mb-4">
              Your online debit order request has been sent to your bank. Please approve it on your banking app.
            </p>
            <Button variant="outline" @click="isAcceptMandateLater = true">Unable To Accept Now</Button>
          </template>
        </template>

        <template v-else-if="debiCheckStatus === 'Accepted'">
          <div class="flex items-center justify-center w-14 h-14 rounded-full border-2 border-teal-400 mb-6">
            <Check class="w-7 h-7 text-teal-400" stroke-width="2.5" />
          </div>
          <p class="text-xl font-bold text-gray-800 mb-2">Mandate Accepted</p>
          <p class="text-gray-500 text-sm mb-4">You have successfully accepted your debit order. Thank you.</p>
          <Button @click="emit('back')">Close</Button>
        </template>

        <template v-else-if="debiCheckStatus === 'Rejected'">
          <div class="flex items-center justify-center w-14 h-14 rounded-full border-2 border-red-400 mb-6">
            <AlertCircle class="w-7 h-7 text-red-400" stroke-width="2.5" />
          </div>
          <p class="text-lg font-bold text-gray-800 mb-2">Mandate Rejected</p>
          <p class="text-gray-500 text-sm mb-1">Please check that your bank details are correct.</p>
          <p v-for="(reason, i) in debicheckFailedReasons" :key="i" class="text-red-600 text-sm font-semibold">
            {{ reason }}
          </p>
          <div class="flex gap-3 mt-4">
            <Button variant="outline" @click="emit('back')">Cancel</Button>
            <Button @click="goBackToForm">Amend Bank Details</Button>
          </div>
        </template>

        <template v-else-if="checkCurrentTime">
          <p class="text-gray-600 mb-4">Your debit order application will be submitted tomorrow at 8am.</p>
          <Button variant="outline" @click="emit('back')">Close</Button>
        </template>

        <template v-else>
          <div class="flex items-center justify-center w-14 h-14 rounded-full border-2 border-red-400 mb-6">
            <AlertCircle class="w-7 h-7 text-red-400" stroke-width="2.5" />
          </div>
          <p class="text-red-500 font-semibold mb-1">DebiCheck creation failed.</p>
          <p v-for="(reason, i) in debicheckFailedReasons" :key="i" class="text-sm font-semibold">{{ reason }}</p>
          <Button variant="outline" class="mt-4" @click="emit('back')">Close</Button>
        </template>

        <template v-if="isSimulation && step === 'confirmation'">
          <hr class="w-full mt-6" />
          <p class="mt-4 font-semibold text-sm">DEBICHECK SIMULATION RESPONSES:</p>
          <div
            v-if="simulationSubmitted"
            class="mt-2 animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-teal-500"
          />
          <div v-else class="flex gap-3 mt-3">
            <Button @click="simulateResponse('Accepted')">Approve</Button>
            <Button variant="destructive" @click="simulateResponse('Rejected')">Reject</Button>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ENVIRONMENT } from '@/config';
import { NetworkError } from '@/errors';
import { getMe } from '@/services/auth';
import { fetchBanks } from '@/services/banks';
import { createMandate, getMandate, getMandates, simulateMandateResponse } from '@/services/debiCheck';
import { getOrderByIdWithPlansAndPayments } from '@/services/orders';
import { currency } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { nextPayment } from '@/utils/paymentPlan.utils';
import { toTypedSchema } from '@vee-validate/zod';
import dayjs from 'dayjs';
import { AlertCircle, ArrowLeft, Check } from 'lucide-vue-next';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { z } from 'zod';
import {
  ModelDebiCheckAccountTypeEnum,
  ModelDebiCheckIdentificationTypeEnum,
  ModelDebiCheckMediumEnum,
  type ModelDebiCheckMandateListResponse,
  type ModelDebiCheckMandateResponse,
  type ModelDebiCheckRequest,
  type ModelOrderResponse,
  type ModelPaymentPlanResponse,
} from '@layup/api-sdk';

const props = defineProps<{
  order: ModelOrderResponse;
  amountCents: number;
}>();

const emit = defineEmits<{ back: [] }>();

type FormStep = 'form' | 'confirmation';

const step = ref<FormStep>('form');
const isLoading = ref(false);
const errorMessage = ref('');
const acceptTerms = ref(false);
const allowSimulation = ref(false);
const bankSelection = ref<{ value: string; text: string; branchCode: string; name: string }[]>([]);
const mandateRequest = ref<ModelDebiCheckRequest | null>(null);

const mandateId = ref('');
const mandate = ref<ModelDebiCheckMandateResponse>({});
const mandateList = ref<ModelDebiCheckMandateListResponse>({});
const isAcceptMandateLater = ref(false);
const simulationSubmitted = ref(false);
const countdownDate = ref(dayjs().subtract(dayjs().hour() - 21, 'hours'));
const timerCount = ref(120);

const formSchema = toTypedSchema(
  z.object({
    identificationNumber: z.string().min(1, 'ID number is required'),
    firstName: z.string().min(1, 'First name is required'),
    surname: z.string().min(1, 'Surname is required'),
    cellNumber: z.string().min(1, 'Cell number is required'),
    bankName: z.string().min(1, 'Bank name is required'),
    accountNumber: z.string().min(1, 'Account number is required'),
    branchCode: z.string().min(1, 'Branch code is required'),
    instalmentAmount: z.string().min(1, 'Instalment amount is required'),
    billingDay: z.string().min(1, 'Billing day is required'),
    identityType: z.string().min(1, 'Identity type is required'),
    accountType: z.string().min(1, 'Account type is required'),
  }),
);

const form = useForm({ validationSchema: formSchema });

const splitCamelCase = (text: string) => text.replace(/([a-z])([A-Z])/g, '$1 $2');

const identificationSelection = computed(() =>
  Object.keys(ModelDebiCheckIdentificationTypeEnum).map((k, i) => ({ value: i.toString(), text: splitCamelCase(k) })),
);

const accountTypeSelection = computed(() =>
  Object.keys(ModelDebiCheckAccountTypeEnum).map((k, i) => ({ value: i.toString(), text: splitCamelCase(k) })),
);

watch(
  () => form.values.bankName,
  (val) => {
    const selected = bankSelection.value.find((b) => b.value === val);

    if (selected) {
      form.setValues({ branchCode: selected.branchCode });
    }
  },
);

const handleError = (error: any, context = 'fetch') => {
  const base =
    context === 'fetch'
      ? 'Could not fetch data. Please try again later.'
      : 'Could not create DebiCheck. Please try again later.';
  errorMessage.value = error?.response?.data || base;

  if (error instanceof NetworkError) {
    createWarning(error);
    addMetric('debi-check', { status: 'network_error', channel: 'shopper_contribute' });
  } else {
    createException(error);
    addMetric('debi-check', { status: 'error', channel: 'shopper_contribute' });
  }
};

const init = async () => {
  isLoading.value = true;

  try {
    const [order, user, bankResponse] = await Promise.all([
      getOrderByIdWithPlansAndPayments(props.order._id!),
      getMe(),
      fetchBanks(),
    ]);

    const plan = order.plans?.[0] as ModelPaymentPlanResponse | undefined;
    const existingMandateIds = (plan as any)?.debicheckMandateIds as string[] | undefined;

    if (existingMandateIds?.length) {
      const latestId = existingMandateIds[existingMandateIds.length - 1];
      const [mandateListRes, mandateRes] = await Promise.all([getMandates(latestId), getMandate(latestId)]);

      if (mandateListRes.status !== 'Rejected') {
        mandateId.value = latestId;
        mandate.value = mandateRes;
        mandateList.value = mandateListRes;
        step.value = 'confirmation';
        return;
      }
    }

    const paymentPlan = order.plans?.[0] as ModelPaymentPlanResponse | undefined;
    const nextDuePayment = nextPayment(paymentPlan);

    if (nextDuePayment) {
      form.setValues({
        instalmentAmount: currency(nextDuePayment.amount),
        billingDay: dayjs(nextDuePayment.due).format('YYYY-MM-DD'),
        identityType: '0',
        accountType: '0',
      });
    }

    if (user) {
      const [firstName, surname] = user.name?.split(' ') || [];
      form.setValues({
        firstName,
        surname,
        cellNumber: user.cellNumber,
        identificationNumber: user.idNumber,
      });

      if (user.bankDetails?.bankName) {
        const matched = bankResponse.banks?.find((b: any) => b.name === user.bankDetails?.bankName?.value);

        if (matched) {
          const idx = bankSelection.value.findIndex((b) => b.name === matched.name);
          if (idx >= 0) form.setValues({ bankName: String(idx), branchCode: matched.branchCode });
        }
      }
    }

    bankSelection.value =
      bankResponse.banks?.map((b: any, i: number) => ({
        value: String(i),
        text: b.displayName,
        branchCode: b.branchCode,
        name: b.name,
      })) || [];

    if (bankSelection.value.length > 0) {
      form.setValues({ bankName: bankSelection.value[0].value, branchCode: bankSelection.value[0].branchCode });
    }

    const planId = props.order.plans?.[0]?._id;

    if (mandateRequest.value?.accountNumber && mandateRequest.value?.paymentPlanId === planId) {
      form.setValues({
        accountNumber: mandateRequest.value.accountNumber,
        accountType: Object.values(ModelDebiCheckAccountTypeEnum)
          .findIndex((a) => a === mandateRequest.value?.accountType)
          .toString(),
        identityType: Object.values(ModelDebiCheckIdentificationTypeEnum)
          .findIndex((a) => a === mandateRequest.value?.identificationType)
          .toString(),
        branchCode: mandateRequest.value.branchCode,
        firstName: mandateRequest.value.name?.split(' ')[0],
        surname: mandateRequest.value.name?.split(' ')[1],
        cellNumber: mandateRequest.value.contact?.[0]?.value,
        identificationNumber: mandateRequest.value.identification,
        instalmentAmount: currency(paymentPlan?.payments?.[0]?.amount || 0),
        billingDay: dayjs(paymentPlan?.payments?.[0]?.due).format('YYYY-MM-DD'),
        bankName: String(bankSelection.value.findIndex((b) => b.branchCode === mandateRequest.value?.branchCode)),
      });
    }
  } catch (error: any) {
    handleError(error);
  } finally {
    isLoading.value = false;
  }
};

const onSubmit = async () => {
  const { values, errors, valid } = await form.validate();

  if (Object.keys(errors).length > 0) {
    Object.entries(errors).forEach(([key, value]) => form.setFieldError(key as any, value));
    return;
  }

  if (!valid) return;

  isLoading.value = true;

  try {
    const request: ModelDebiCheckRequest = {
      name: `${values?.firstName} ${values?.surname}`,
      identification: values?.identificationNumber,
      accountType: Object.values(ModelDebiCheckAccountTypeEnum)[Number(values?.accountType)],
      branchCode: values?.branchCode,
      identificationType: Object.values(ModelDebiCheckIdentificationTypeEnum)[Number(values?.identityType)],
      paymentPlanId: props.order.plans?.[0]?._id,
      simulation: allowSimulation.value,
      contact: [{ medium: ModelDebiCheckMediumEnum.PhoneNumber, value: values?.cellNumber }],
      accountNumber: values?.accountNumber,
    };
    mandateRequest.value = request;

    const response = await createMandate(request);
    mandateId.value = response._id!;

    await loadConfirmation();
    step.value = 'confirmation';
  } catch (error: any) {
    const message: string = error?.response?.data ?? error?.message ?? '';
    const existing = message.match(/Mandate with ID:\s*([a-f0-9]+)\s*has already been initiated/i);

    if (existing?.[1]) {
      mandateId.value = existing[1];
      await loadConfirmation();
      step.value = 'confirmation';
    } else {
      handleError(error, 'submit');
    }
  } finally {
    isLoading.value = false;
  }
};

const debiCheckStatus = computed(() => mandateList.value.status ?? null);
const debicheckFailedReasons = computed(() => mandateList.value.debicheckFailedReasons);
const isSimulation = computed(() => mandate.value.simulation);

const checkCurrentTime = computed(() => {
  const now = dayjs(mandate.value.createdAt || new Date());
  const hourToCheck = now.day() !== 0 ? 20 : 30;

  return dayjs().isAfter(now.hour(hourToCheck).minute(30));
});

const countdownTime = computed(() => countdownDate.value.format('hh:mm:ss'));

const loadConfirmation = async () => {
  const [mandateListRes, mandateRes] = await Promise.all([getMandates(mandateId.value), getMandate(mandateId.value)]);

  mandate.value = mandateRes;
  mandateList.value = mandateListRes;
};

const fetchMandateList = async () => {
  mandateList.value = await getMandates(mandateId.value);
};

watch(
  debiCheckStatus,
  (newStatus) => {
    setInterval(() => {
      countdownDate.value = dayjs(countdownDate.value).subtract(1, 'seconds');

      if (timerCount.value > 0) {
        timerCount.value--;
      }
    }, 1000);

    let pollingHandle: ReturnType<typeof setInterval> | null = setInterval(fetchMandateList, 5000);

    setTimeout(() => {
      if (pollingHandle) {
        clearInterval(pollingHandle);
      }
    }, 120000);

    if (newStatus === 'Pending Authorisation') {
      if (pollingHandle) {
        clearInterval(pollingHandle);
      }

      const extended = setInterval(fetchMandateList, 15000);
      setTimeout(() => clearInterval(extended), 1800000);
    }

    if (newStatus === 'Rejected') {
      if (pollingHandle) {
        clearInterval(pollingHandle);
      }
    }

    if (newStatus === 'Accepted') {
      mandateRequest.value = null;

      setTimeout(() => {
        if (pollingHandle) clearInterval(pollingHandle);
      }, 5000);
    }
  },
  { immediate: true },
);

const simulateResponse = async (status: 'Accepted' | 'Rejected') => {
  simulationSubmitted.value = true;

  await simulateMandateResponse(mandateId.value, status);
  await fetchMandateList();

  simulationSubmitted.value = false;
};

const goBackToForm = () => {
  step.value = 'form';
};

init();
</script>
